import React from "react";
import {
  CreditCard, Smartphone, Receipt, FileText, Activity,
  Zap, ShieldCheck, MousePointerClick, DollarSign, Sparkles,
} from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, Lead, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  ParallelGrid, FlowDiagram, FeatureCallout, ChecklistCard,
  SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogInOfficePaymentsUnified = () => (
  <RichBlogLayout
    category="Patient Billing"
    eyebrow="Payments"
    title="When the Card Reader Is the PMS: How In-Office Payments Become a Single Step"
    subtitle="From three steps (terminal → manual entry → reconciliation) to one continuous workflow inside the chart, in real time."
    author="Asha Team"
    date="May 2, 2026"
    readTime="8 min read"
  >
    <Lead>
      A patient pays $312.40 at the front desk on a Friday afternoon. The card
      runs on the terminal. The receipt prints. Two hours later, in the rush
      to close, someone forgets to mark it in the PMS. By Monday, the patient
      is on the AR aging report. By Tuesday, they get a "your balance is
      overdue" text. By Wednesday, they're frustrated.
    </Lead>

    <Prose>
      <p>
        It is not that anyone was lazy. It is that the workflow had three
        independent moving parts: the terminal, the PMS ledger, and the
        merchant dashboard. Three systems, three steps, three places for
        truth to live, and only one moment in the day where someone has to
        stitch them all together. When a practice gets busy, that stitch is
        the first thing to slip.
      </p>
      <p>
        Asha removes the stitch by removing the steps. The card reader isn't
        a separate piece of hardware connected to the PMS — it is the PMS.
        Tapping a card and posting a payment are the same action.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 1, label: "Continuous workflow (was three disconnected steps)" },
        { value: 0, label: "Manual ledger entries after a card swipe" },
        { value: 0, label: "End-of-day reconciliation tasks" },
        { value: 4, label: "Payment methods supported (Apple Pay, Google Pay, Chip, Swipe)" },
      ]}
    />

    <Prose>
      <h2>Three systems pretending to be one</h2>
      <p>
        The traditional checkout flow is well understood, which is part of
        the problem. Everyone has accepted it as the cost of doing business.
        But it's worth naming what each step actually is.
      </p>
    </Prose>

    <BeforeAfter
      beforeTitle="The traditional 3-step flow"
      afterTitle="The single-step Asha flow"
      beforeItems={[
        "Run the card on a standalone terminal — receipt prints separately",
        "Manually mark the payment in the PMS ledger",
        "Reconcile against the merchant dashboard at end of day or week",
        "When something is missed, balances drift and patients get billed twice",
      ]}
      afterItems={[
        "Front desk clicks 'Collect' from the patient's billing tab",
        "Stripe terminal activates inline — patient taps Apple Pay, Google Pay, Chip, or Swipe",
        "Receipt, ledger, and claim all update in real time on the chart",
        "Nothing to reconcile because there's only one record of truth",
      ]}
    />

    <Prose>
      <h2>What "the card reader is the PMS" actually means</h2>
      <p>
        The terminal is not a peripheral. It is wired into the same record
        the chart writes to, which means a payment never has to be retyped,
        re-categorized, or re-matched against a claim. The transaction event
        from Stripe arrives at the same surface that holds the ledger.
      </p>
    </Prose>

    <FlowDiagram
      title="The single-step workflow"
      nodes={[
        { label: "Click Collect", sublabel: "From patient's billing tab", icon: MousePointerClick },
        { label: "Tap card on terminal", sublabel: "Apple Pay, Google Pay, Chip, Swipe", icon: CreditCard },
        { label: "Receipt + Ledger + Claim", sublabel: "Updated in real time", icon: Receipt },
      ]}
    />

    <Prose>
      <p>
        From the patient's seat, it looks like every other tap-to-pay moment
        they've had this week. From the front desk's seat, an entire
        downstream workflow happened with no second action. The receipt is
        already generated. The ledger is already updated. The claim is
        already marked paid. There is no end-of-day cleanup queue because
        there is no cleanup.
      </p>
    </Prose>

    <PullQuote>
      The card reader isn't separate from the system. It is the PMS. So
      everything happens in one continuous workflow.
    </PullQuote>

    <Prose>
      <h2>What runs in parallel the moment the card taps</h2>
      <p>
        The single visible action — the tap — kicks off several things at
        once. Each one would normally be a separate step, a separate system,
        or a separate person.
      </p>
    </Prose>

    <ParallelGrid
      title="Triggered by a single card tap"
      columns={3}
      items={[
        { title: "Stripe terminal authorizes", subtitle: "Tap, Chip, or Swipe", icon: CreditCard, status: "done" },
        { title: "Receipt generated", subtitle: "Print, email, or text", icon: Receipt, status: "done" },
        { title: "Ledger updated", subtitle: "On the patient's chart", icon: FileText, status: "done" },
        { title: "Claim marked paid", subtitle: "Insurance + patient split reconciled", icon: ShieldCheck, status: "done" },
        { title: "Linked to encounter", subtitle: "Tied to the visit, not just the date", icon: Activity, status: "done" },
        { title: "Visible to billing team", subtitle: "Searchable, auditable, immediate", icon: DollarSign, status: "done" },
      ]}
    />

    <Prose>
      <p>
        Six things, one action. None of them are jobs anyone has to remember
        to do later. The asymmetry between the patient experience (one tap)
        and the operational benefit (six things resolved) is the entire
        product.
      </p>

      <h2>What the patient sees vs. what the system does</h2>
      <p>
        The patient experience is intentionally boring. They tap. They get a
        receipt the way they want it. They leave. None of the complexity
        beneath the surface is visible to them, and none of it should be.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "Front desk clicks 'Collect' from the chart",
          body:
            "No separate app, no terminal mode to enter, no checkout page to bring up. The action lives where the balance lives.",
        },
        {
          title: "Stripe terminal prompts: tap, insert, or swipe",
          body:
            "The patient pays however they prefer. Apple Pay, Google Pay, Chip, Swipe — all supported on the same hardware.",
        },
        {
          title: "Authorization comes back to the same chart surface",
          body:
            "There is no separate confirmation step. The PMS already knows the payment posted because it issued the request.",
        },
        {
          title: "Receipt is delivered the way the patient prefers",
          body:
            "Printed, emailed, or texted — all from the same chart action, no second tool to open.",
        },
        {
          title: "Ledger and claim status update live",
          body:
            "Insurance portion, patient portion, and remaining balance all reconcile in real time. Nothing to fix later.",
        },
      ]}
    />

    <Prose>
      <h2>The end-of-day check that never has to happen</h2>
      <p>
        In a traditional setup, the closing checklist always includes some
        version of "reconcile today's payments." Match the merchant
        dashboard. Find the missing entries. Chase the front desk for the
        ones that didn't get logged. It's quiet work that pulls a manager
        away from the part of the job that actually moves the practice
        forward.
      </p>
      <p>
        With payments unified into the chart, the reconciliation step is
        redundant by construction. Every transaction is already tied to the
        patient, the claim, and the encounter — at the moment it happens.
        Nothing is sitting in a queue waiting to be matched.
      </p>
    </Prose>

    <ChecklistCard
      title="Closing checklist · in-office payments"
      items={[
        { label: "Card terminal totals match the PMS ledger", status: "pass" },
        { label: "Every payment linked to an encounter", status: "pass" },
        { label: "Insurance vs. patient split reconciled", status: "pass" },
        { label: "Receipts delivered to every patient", status: "pass" },
      ]}
    />

    <Prose>
      <p>
        Every line passes by default, because every line was already true
        the moment the card tapped. The closing routine becomes a glance,
        not a task.
      </p>
    </Prose>

    <FeatureCallout
      stat="3 → 1"
      label="From three disconnected systems to one continuous workflow"
      description="Every in-office payment is tied to the patient, the claim, and the ledger in real time — no separate terminal, no manual entry, no end-of-day reconciliation."
      icon={Zap}
    />

    <Prose>
      <h2>What changes for the front desk</h2>
      <p>
        The front desk's job at checkout becomes simpler in a way that's
        easy to undersell. No "did I mark it in the PMS?" hesitation. No
        "let me run the card while I find the chart" sequence. No "I'll
        reconcile this at the end of the day" mental note. The action they
        wanted to take — collect the payment — is the only action they
        actually take.
      </p>
      <p>
        The downstream effect is that the billing team stops getting
        Monday-morning surprises. There's no AR aging report bloated with
        already-paid balances. There's no chasing patients for money the
        practice already received. The "we already paid that" call —
        every billing team's least favorite — quietly disappears.
      </p>
    </Prose>

    <FlowDiagram
      title="What disappears from the workday"
      nodes={[
        { label: "End-of-day reconciliation", sublabel: "No mismatched totals", icon: ShieldCheck },
        { label: "AR aging cleanup", sublabel: "No false balances", icon: FileText },
        { label: "Patient billing disputes", sublabel: "From mis-posted payments", icon: Smartphone },
      ]}
    />

    <Prose>
      <p>
        These aren't features. They are categories of work that no longer
        need to exist because the underlying system stopped manufacturing
        the problem.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        Most "integrated" payment products are still really three systems
        held together with sync logic. Asha's in-office payments collapse
        the terminal, the ledger, and the merchant dashboard into a single
        chart action — which means the work to keep them in sync is no work
        at all.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogInOfficePaymentsUnified;
